import React, { useState, useEffect } from 'react'
import ServiceTitleAutocomplete from './ServiceTitleAutocomplete'

const emptyEntry = {
  title: '',
  description: '',
  cost: '',
  next_service_meter: '',
  next_service_date: '',
}

const emptyVisit = {
  service_date: new Date().toISOString().split('T')[0],
  meter_at_service: '',
  shop_name: '',
}

export default function AddMaintenanceModal({ isOpen, onClose, onSave, editVisit, meterLabel = 'mi', premium = true, onUpgrade }) {
  const [visit, setVisit] = useState(emptyVisit)
  const [entries, setEntries] = useState([{ ...emptyEntry }])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [submitted, setSubmitted] = useState(false)
  const [newFiles, setNewFiles] = useState([])
  const [existingFiles, setExistingFiles] = useState([])
  const [fileIdsToRemove, setFileIdsToRemove] = useState([])

  useEffect(() => {
    if (editVisit) {
      setVisit({
        service_date: editVisit.service_date || '',
        meter_at_service: editVisit.meter_at_service || '',
        shop_name: editVisit.shop_name || '',
      })
      setEntries(
        editVisit.service_entries.map((e) => ({
          id: e.id,
          title: e.title || '',
          description: e.description || '',
          cost: e.cost || '',
          next_service_meter: e.next_service_meter || '',
          next_service_date: e.next_service_date || '',
        }))
      )
      setExistingFiles(editVisit.files || [])
      setFileIdsToRemove([])
    } else {
      setVisit(emptyVisit)
      setEntries([{ ...emptyEntry }])
      setExistingFiles([])
      setFileIdsToRemove([])
    }
    setNewFiles([])
    setError(null)
    setSubmitted(false)
  }, [editVisit, isOpen])

  if (!isOpen) return null

  const handleVisitChange = (e) => {
    setVisit({ ...visit, [e.target.name]: e.target.value })
  }

  const handleEntryChange = (index, e) => {
    const updated = [...entries]
    updated[index] = { ...updated[index], [e.target.name]: e.target.value }
    setEntries(updated)
  }

  const addEntry = () => {
    setEntries([...entries, { ...emptyEntry }])
  }

  const removeEntry = (index) => {
    const entry = entries[index]
    if (entry.id) {
      const updated = [...entries]
      updated[index] = { ...updated[index], _destroy: true }
      setEntries(updated)
    } else {
      setEntries(entries.filter((_, i) => i !== index))
    }
  }

  const handleFileAdd = (e) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf']
    const incoming = Array.from(e.target.files)
    const invalid = incoming.filter(f => !allowedTypes.includes(f.type))
    const tooLarge = incoming.filter(f => f.size > 5 * 1024 * 1024)

    if (invalid.length) {
      alert('Files must be images (JPEG, PNG, WebP) or PDFs')
      e.target.value = ''
      return
    }
    if (tooLarge.length) {
      alert('Each file must be less than 5MB')
      e.target.value = ''
      return
    }
    if (newFiles.length + existingFiles.length + incoming.length > 10) {
      alert('Maximum 10 files per maintenance log')
      e.target.value = ''
      return
    }
    setNewFiles([...newFiles, ...incoming])
    e.target.value = ''
  }

  const removeNewFile = (index) => {
    setNewFiles(newFiles.filter((_, i) => i !== index))
  }

  const removeExistingFile = (fileId) => {
    setFileIdsToRemove([...fileIdsToRemove, fileId])
    setExistingFiles(existingFiles.filter((f) => f.id !== fileId))
  }

  const isFieldMissing = (val) => submitted && !val

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitted(true)

    const hasEmptyTitles = entries.some((ent) => !ent._destroy && !ent.title.trim())
    if (!visit.service_date || !visit.meter_at_service || hasEmptyTitles) return

    setSaving(true)
    setError(null)

    try {
      const formData = new FormData()
      const prefix = 'maintenance_log'

      formData.append(`${prefix}[service_date]`, visit.service_date)
      formData.append(`${prefix}[meter_at_service]`, parseInt(visit.meter_at_service))
      if (visit.shop_name) formData.append(`${prefix}[shop_name]`, visit.shop_name)

      entries.forEach((entry, i) => {
        const ep = `${prefix}[service_entries_attributes][${i}]`
        formData.append(`${ep}[title]`, entry.title)
        if (entry.description) formData.append(`${ep}[description]`, entry.description)
        if (entry.cost) formData.append(`${ep}[cost]`, parseFloat(entry.cost))
        if (entry.next_service_meter) formData.append(`${ep}[next_service_meter]`, parseInt(entry.next_service_meter))
        if (entry.next_service_date) formData.append(`${ep}[next_service_date]`, entry.next_service_date)
        if (entry.id) formData.append(`${ep}[id]`, entry.id)
        if (entry._destroy) formData.append(`${ep}[_destroy]`, true)
      })

      newFiles.forEach((file) => {
        formData.append(`${prefix}[files][]`, file)
      })

      fileIdsToRemove.forEach((id) => {
        formData.append(`${prefix}[file_ids_to_remove][]`, id)
      })

      await onSave(formData, editVisit?.id)
      onClose()
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.upgrade_required) {
        onUpgrade?.()
        setError(err.response.data.error)
        return
      }
      setError(err.response?.data?.errors?.join(', ') || err.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const visibleEntries = entries.filter((e) => !e._destroy)
  const inputClass = "w-full min-w-0 border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-accent focus:border-accent"

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-surface border-[1.5px] border-ink shadow-lg w-full max-w-2xl mx-4 max-h-[90dvh] overflow-y-auto overflow-x-hidden">
        <div className="px-6 py-4 border-b border-line">
          <h3 className="text-xl font-extrabold">
            {editVisit ? 'Edit Maintenance Log' : 'Add Maintenance Log'}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4 overflow-hidden">
          {error && (
            <div className="text-sm text-accent-dark bg-accent/10 border border-accent/40 p-3">{error}</div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 overflow-hidden">
            <div className="overflow-hidden">
              <label className="block text-sm font-medium text-gray-700 mb-1">Service Date *</label>
              <input
                name="service_date"
                type="date"
                value={visit.service_date}
                onChange={handleVisitChange}
                className={`${inputClass} ${isFieldMissing(visit.service_date) ? 'border-accent' : ''}`}
              />
              {isFieldMissing(visit.service_date) && <p className="text-xs text-accent-dark mt-1">Required</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{meterLabel === 'hrs' ? 'Hours' : 'Odometer'} ({meterLabel}) *</label>
              <input
                name="meter_at_service"
                type="number"
                value={visit.meter_at_service}
                onChange={handleVisitChange}
                className={`${inputClass} ${isFieldMissing(visit.meter_at_service) ? 'border-accent' : ''}`}
              />
              {isFieldMissing(visit.meter_at_service) && <p className="text-xs text-accent-dark mt-1">Required</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Shop Name</label>
              <input
                name="shop_name"
                value={visit.shop_name}
                onChange={handleVisitChange}
                className={inputClass}
              />
            </div>
          </div>

          <hr className="border-gray-200" />

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-700">Services Performed</h4>
              <button
                type="button"
                onClick={addEntry}
                className="text-sm text-accent hover:text-accent-dark font-medium"
              >
                + Add Another Service
              </button>
            </div>

            {entries.map((entry, index) => {
              if (entry._destroy) return null
              return (
                <div key={entry.id || index} className="border border-gray-200 rounded-md p-4 space-y-3 overflow-hidden">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-400 uppercase">Service {visibleEntries.indexOf(entry) + 1}</span>
                    {visibleEntries.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEntry(index)}
                        className="text-xs text-accent-dark hover:text-accent"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <ServiceTitleAutocomplete
                      value={entry.title}
                      onChange={(e) => handleEntryChange(index, e)}
                      className={`${inputClass} ${isFieldMissing(entry.title.trim()) ? 'border-accent' : ''}`}
                      placeholder="e.g., Oil Change"
                    />
                    {isFieldMissing(entry.title.trim()) && <p className="text-xs text-accent-dark mt-1">Required</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      name="description"
                      value={entry.description}
                      onChange={(e) => handleEntryChange(index, e)}
                      rows={2}
                      className={inputClass}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 overflow-hidden">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cost ($)</label>
                      <input
                        name="cost"
                        type="number"
                        step="0.01"
                        value={entry.cost}
                        onChange={(e) => handleEntryChange(index, e)}
                        className={inputClass}
                      />
                    </div>
                    <div className="overflow-hidden">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Next Service Date</label>
                      <input
                        name="next_service_date"
                        type="date"
                        value={entry.next_service_date}
                        onChange={(e) => handleEntryChange(index, e)}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Next Service ({meterLabel})</label>
                      <input
                        name="next_service_meter"
                        type="number"
                        value={entry.next_service_meter}
                        onChange={(e) => handleEntryChange(index, e)}
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          <hr className="border-gray-200" />

          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Attachments</h4>

            {existingFiles.length > 0 && (
              <div className="space-y-1 mb-3">
                {existingFiles.map((file) => (
                  <div key={file.id} className="flex items-center justify-between text-sm bg-gray-50 rounded px-3 py-1.5">
                    <span className="text-gray-700 truncate">{file.filename}</span>
                    <button
                      type="button"
                      onClick={() => removeExistingFile(file.id)}
                      className="text-xs text-accent-dark hover:text-accent ml-2 shrink-0"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            {newFiles.length > 0 && (
              <div className="space-y-1 mb-3">
                {newFiles.map((file, i) => (
                  <div key={i} className="flex items-center justify-between text-sm bg-surface rounded px-3 py-1.5">
                    <span className="text-gray-700 truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeNewFile(i)}
                      className="text-xs text-accent-dark hover:text-accent ml-2 shrink-0"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            {premium ? (
              <label className="inline-flex items-center gap-2 text-sm text-accent hover:text-accent-dark font-medium cursor-pointer">
                <input
                  type="file"
                  multiple
                  onChange={handleFileAdd}
                  className="hidden"
                />
                + Add Files
              </label>
            ) : (
              <button
                type="button"
                onClick={() => onUpgrade?.()}
                className="inline-flex items-center gap-2 text-sm text-accent hover:text-accent-dark font-medium"
              >
                + Add Files (Premium)
              </button>
            )}
            <p className="text-xs text-gray-400 mt-1">Receipts, invoices, photos, etc.</p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-bold text-white bg-accent rounded-md hover:bg-accent-dark disabled:opacity-50"
            >
              {saving ? 'Saving...' : (editVisit ? 'Update Log' : 'Add Log')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
